import React, { useEffect, useState } from 'react';
import Stepper from '../../components/common/Stepper';
import AdminLayout from '../../components/layout/AdminLayout';
import Otp from '../../components/auth/Otp';
import {
  MemberCategoryDTO,
  MemberDTO,
  MemberTypeDTO,
  MemberFeeAmountDTO,
  InvoiceDTO,
} from '../../services/apiClient';
import { useMemberApi } from '../../hooks/useMemberApi';
import { getMemberId, getRefreshToken } from '../../utils/tokenUtils';
import { MemberRegistrationStepConstants } from '../../constants/MemberRegistrationStepConstants';
import Welcome from '../../components/steps/member-setup/Welcome';
import Membership from '../../components/steps/member-setup/Membership';
import { useMemberTypeById } from '../../hooks/useMemberTypeApi';
import { CountryConstant } from '../../constants/CountryConstants';
import { extractBirthdateFromSAId } from '../../utils/dateUtils/birthDateFromSAId';
import { calculateAge } from '../../utils/calculateAge';
import { MemberTypeConstants } from '../../constants/MemberTypeConstants';
import { useNavigate } from 'react-router-dom';
import { useAuthApi } from '../../hooks/useAuthApi';
import Payment, { PaymentCallbacks, PaymentConfig, PaymentStatus } from '../../components/drawer/Payment';
import { ComponentConstants } from '../../constants/ComponentConstants';
import { usePayFastApi } from '../../hooks/usePayFastApi';
import { useInvoiceApi } from '../../hooks/useInvoiceApi';

const MemberSetup: React.FC = () => {
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Member Setup', href: '#' },
  ];

  // Create mapping between GUIDs and step numbers
  const stepMapping = {
    [MemberRegistrationStepConstants.OTP]: 1,
    [MemberRegistrationStepConstants.MEMBERSHIP]: 2,
    [MemberRegistrationStepConstants.WELCOME]: 3,
    [MemberRegistrationStepConstants.PAYMENT]: 4,
    [MemberRegistrationStepConstants.THREE_D_REDIRECT]: 4,
    [MemberRegistrationStepConstants.PROOF_PENDING]: 4,
    [MemberRegistrationStepConstants.PROOF_UPLOADED]: 4,
    [MemberRegistrationStepConstants.PAYMENT_FAILED]: 4,
    [MemberRegistrationStepConstants.COMPLETE]: 5,
  };

  // Reverse mapping for converting step numbers back to GUIDs
  const numberToGuidMapping = {
    1: MemberRegistrationStepConstants.OTP,
    2: MemberRegistrationStepConstants.MEMBERSHIP,
    3: MemberRegistrationStepConstants.WELCOME,
    4: MemberRegistrationStepConstants.PAYMENT,
    5: MemberRegistrationStepConstants.COMPLETE,
  };

  const { refreshUserToken, isLoading } = useAuthApi();
  const refreshToken = getRefreshToken(); // Automatically checks sessionStorage first, then localStorage
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberDTO | undefined>({} as MemberDTO);
  const [memberType, setMemberType] = useState<MemberTypeDTO | undefined>();
  const [memberTypeFee, setMemberTypeFee] = useState<MemberFeeAmountDTO>();
  const [memberCategory, setMemberCategory] = useState<MemberCategoryDTO | undefined>();
  const [payFastFee, setPayFastFee] = useState<number | undefined>();
  const [totalWithPayFastFee, setTotalWithPayFastFee] = useState<number | undefined>();
  const [isCalculatingFees, setIsCalculatingFees] = useState<boolean>(false);
  const [existingMemberInvoice, setExistingMemberInvoice] = useState<InvoiceDTO | undefined>();

  const { getById, updateRegistrationStep, sendProofOfPaymentLink, updateProofOfPayment } = useMemberApi();
  const { calculateFee } = usePayFastApi();
  const { search: searchInvoices } = useInvoiceApi();

  const memberId = getMemberId();

  // Helper function to determine memberTypeId based on age
  const getMemberTypeId = (member: MemberDTO | undefined): string => {
    if (!member) return '';

    let memberAge = 0;
    let memberTypeId = '';

    if (
      member.idPassportNumber &&
      member.countryId === CountryConstant.SOUTH_AFRICA
    ) {
      const birthdateFromId = extractBirthdateFromSAId(member.idPassportNumber);
      if (birthdateFromId) {
        memberAge = calculateAge(birthdateFromId);
      }
    } else if (member.dateOfBirth) {
      const birthDate = new Date(member.dateOfBirth);
      memberAge = calculateAge(birthDate);
    }

    if (memberAge <= 20) {
      return (memberTypeId = MemberTypeConstants.JUNIOR_MEMBER);
    } else if (memberAge >= 21 && memberAge <= 59) {
      return (memberTypeId = MemberTypeConstants.REGULAR_MEMBER);
    } else if (memberAge >= 60) {
      return (memberTypeId = MemberTypeConstants.SENIOR_MEMBER);
    }

    return memberTypeId;
  };

  const [steps, setSteps] = useState([
    'OTP',
    'Welcome',
    'Membership',
    'Payment',
    'Finish',
  ]);

  // Calculate memberTypeId and conditionally fetch (only for MEMBERSHIP step)
  const memberTypeId = 
    member?.memberRegistrationStepId === MemberRegistrationStepConstants.MEMBERSHIP
      ? getMemberTypeId(member)
      : '';
  const { data: fetchedMemberTypeData } = useMemberTypeById(memberTypeId);

  // Convert member's GUID step to number for Stepper component
  const currentStepNumber = member?.memberRegistrationStepId
    ? stepMapping[member.memberRegistrationStepId] || 1
    : 1;

  // Only the registration step is sent - never the whole member - so a stale tab can't overwrite status/payment data.
  // The server rejects the change once the member is no longer a new registration; we then reload the real state
  // (which redirects to the dashboard if registration is already complete). Returns whether the step was saved.
  const updateMemberRegistrationStep = async (stepId: string, fileRecordId?: string): Promise<boolean> => {
    if (!member?.id) return false;

    try {
      await updateRegistrationStep(member.id, {
        memberRegistrationStepId: stepId,
        ...(fileRecordId && { proofOfPaymentFileId: fileRecordId }),
      });
    } catch (error) {
      console.log('Error updating member registration step', error);
      await fetchMember();
      return false;
    }

    setMember((m) => (m ? {
      ...m,
      memberRegistrationStepId: stepId,
      ...(fileRecordId && { proofOfPaymentFileId: fileRecordId })
    } : m));
    return true;
  };

  const getMemberFee = () => {
    if (!memberType?.memberFee?.memberFeeAmounts || memberType.memberFee.memberFeeAmounts.length === 0) {
      setMemberTypeFee(undefined);
      return;
    }

    const today = new Date();

    const effectiveFees = memberType.memberFee.memberFeeAmounts.filter((fee) => {
      if (!fee.effectiveDate) return false;
      const effectiveDate = new Date(fee.effectiveDate);
      return effectiveDate <= today;
    });

    if (effectiveFees.length === 0) {
      setMemberTypeFee(undefined);
      return;
    }

    const currentFee = effectiveFees.reduce((latest, current) => {
      const latestDate = new Date(latest.effectiveDate!);
      const currentDate = new Date(current.effectiveDate!);
      return currentDate > latestDate ? current : latest;
    });

    setMemberTypeFee(currentFee);
  };

  useEffect(() => {
    getMemberFee();
  }, [memberType]);

  // Calculate PayFast fees when on payment step (handles page refresh)
  useEffect(() => {
    const calculatePayFastFeesOnLoad = async () => {
      const currentStepGuid = member?.memberRegistrationStepId;

      // Only calculate when on payment-related steps
      const isPaymentStep = currentStepGuid === MemberRegistrationStepConstants.PAYMENT ||
        currentStepGuid === MemberRegistrationStepConstants.THREE_D_REDIRECT ||
        currentStepGuid === MemberRegistrationStepConstants.PROOF_PENDING ||
        currentStepGuid === MemberRegistrationStepConstants.PROOF_UPLOADED ||
        currentStepGuid === MemberRegistrationStepConstants.PAYMENT_FAILED;

      if (!isPaymentStep) {
        setPayFastFee(undefined);
        setTotalWithPayFastFee(undefined);
        return;
      }

      // Skip if fees are already calculated
      if (payFastFee !== undefined && totalWithPayFastFee !== undefined) {
        return;
      }

      const baseAmount = (memberTypeFee?.fee || 0) + (memberTypeFee?.joinFee || 0);
      if (baseAmount <= 0) return;

      try {
        const response = await calculateFee(baseAmount);
        if (response.data) {
          setPayFastFee(response.data.payFastFee);
          setTotalWithPayFastFee(response.data.totalWithFee);
        }
      } catch (error) {
        console.error('Error calculating PayFast fees on load:', error);
        setPayFastFee(undefined);
        setTotalWithPayFastFee(undefined);
      }
    };

    calculatePayFastFeesOnLoad();
  }, [member?.memberRegistrationStepId, memberTypeFee]);

  // Look up any invoice already created for this member's registration (e.g. from a prior
  // attempt) so Payment.tsx can independently verify the PayFast fee against a real invoice
  // total instead of only trusting the locally-computed fee above.
  useEffect(() => {
    const findExistingInvoice = async () => {
      const currentStepGuid = member?.memberRegistrationStepId;
      const isPaymentStep = currentStepGuid === MemberRegistrationStepConstants.PAYMENT ||
        currentStepGuid === MemberRegistrationStepConstants.THREE_D_REDIRECT ||
        currentStepGuid === MemberRegistrationStepConstants.PROOF_PENDING ||
        currentStepGuid === MemberRegistrationStepConstants.PROOF_UPLOADED ||
        currentStepGuid === MemberRegistrationStepConstants.PAYMENT_FAILED;

      if (!isPaymentStep || !member?.id) {
        setExistingMemberInvoice(undefined);
        return;
      }

      try {
        const response = await searchInvoices({
          filters: { memberId: member.id, adminComponentId: ComponentConstants.MEMBERS, isDeleted: false },
          page: 1,
          pageSize: 1,
          sortBy: 'CreatedUtc',
          sortDescending: true,
        });
        setExistingMemberInvoice(response?.data?.results?.[0] ?? undefined);
      } catch (error) {
        console.error('Error looking up existing invoice for member:', error);
        setExistingMemberInvoice(undefined);
      }
    };

    findExistingInvoice();
  }, [member?.memberRegistrationStepId, member?.id]);

  // Handler to calculate PayFast fees and move to payment step
  const handleProceedToPayment = async () => {
    setIsCalculatingFees(true);

    try {
      const baseAmount = (memberTypeFee?.fee || 0) + (memberTypeFee?.joinFee || 0);

      if (baseAmount > 0) {
        const response = await calculateFee(baseAmount);
        if (response.data) {
          setPayFastFee(response.data.payFastFee);
          setTotalWithPayFastFee(response.data.totalWithFee);
        }
      }

      // Now transition to payment step
      if (member) {
        setMember({
          ...member,
          memberRegistrationStepId: MemberRegistrationStepConstants.PAYMENT,
        });
      }
    } catch (error) {
      console.error('Error calculating PayFast fees:', error);
      setPayFastFee(undefined);
      setTotalWithPayFastFee(undefined);

      // Still proceed to payment even if fee calculation fails
      if (member) {
        setMember({
          ...member,
          memberRegistrationStepId: MemberRegistrationStepConstants.PAYMENT,
        });
      }
    } finally {
      setIsCalculatingFees(false);
    }
  };

  const renderStepContent = () => {
    const currentStepGuid = member?.memberRegistrationStepId;

    if (!currentStepGuid) {
      return <p>Loading...</p>;
    }

    if (
      currentStepGuid === MemberRegistrationStepConstants.MEMBERSHIP &&
      !memberType?.id
    ) {
      return <p>Loading membership details...</p>;
    }

    switch (currentStepGuid) {
      case MemberRegistrationStepConstants.OTP:
        return (
          <Otp
            member={member}
            onNext={() => {
              if (member) {
                setMember({
                  ...member,
                  memberRegistrationStepId:
                    MemberRegistrationStepConstants.WELCOME,
                });
              }
            }}
          />
        );
      case MemberRegistrationStepConstants.WELCOME:
        return (
          <Welcome
            member={member}
            onStale={fetchMember}
            onNext={() => {
              if (member) {
                setMember({
                  ...member,
                  memberRegistrationStepId:
                    MemberRegistrationStepConstants.MEMBERSHIP,
                });
              }
            }}
          />
        );
      case MemberRegistrationStepConstants.MEMBERSHIP:
        return (
          <Membership
            member={member}
            memberType={memberType}
            memberTypeFee={memberTypeFee}
            isCalculatingFees={isCalculatingFees}
            onNext={handleProceedToPayment}
            onStale={fetchMember}
          />
        );
      case MemberRegistrationStepConstants.PAYMENT:
      case MemberRegistrationStepConstants.THREE_D_REDIRECT:
      case MemberRegistrationStepConstants.PROOF_PENDING:
      case MemberRegistrationStepConstants.PROOF_UPLOADED:
      case MemberRegistrationStepConstants.PAYMENT_FAILED:
        const paymentConfig: PaymentConfig = {
          amount: (memberTypeFee?.fee || 0) + (memberTypeFee?.joinFee || 0),
          itemName: memberType?.title || 'Membership',
          description: memberCategory?.title || '',
          reference: member?.idPassportNumber || '',
          payFastTransactionFee: payFastFee,
          totalWithPayFastFee: totalWithPayFastFee,
        };

        const paymentStatus: PaymentStatus = {
          isProcessing: currentStepGuid === MemberRegistrationStepConstants.THREE_D_REDIRECT,
          isPending: currentStepGuid === MemberRegistrationStepConstants.PROOF_PENDING,
          isUploaded: currentStepGuid === MemberRegistrationStepConstants.PROOF_UPLOADED,
          isFailed: currentStepGuid === MemberRegistrationStepConstants.PAYMENT_FAILED,
        };

        const paymentCallbacks: PaymentCallbacks = {
          onPaymentInitiated: (paymentId) =>
            updateMemberRegistrationStep(MemberRegistrationStepConstants.THREE_D_REDIRECT),
          onProofUploaded: async (fileRecordId) => {
            // Skip the POP record (and admin notification) if registration already completed, e.g. a card payment landed
            const saved = await updateMemberRegistrationStep(MemberRegistrationStepConstants.PROOF_UPLOADED, fileRecordId);
            if (saved) {
              await updateProofOfPayment(member?.id ?? '', fileRecordId);
            }
          },
          onProofPending: async () => {
            try {
              const memberId = getMemberId();
              if (memberId) {
                await sendProofOfPaymentLink(memberId);
              }
            } catch (error) {
              console.error('Error sending proof of payment link:', error);
            }
          },
          onRetryPayment: () => {
            updateMemberRegistrationStep(MemberRegistrationStepConstants.PAYMENT);
          },
          onResetFromProcessing: async () => {
            await updateMemberRegistrationStep(MemberRegistrationStepConstants.PAYMENT);
            window.location.reload();
          },
        };

        return (
          <div className="flex items-center justify-center bg-white">
            <div className="max-w-4xl w-full p-8">
              <Payment
                member={member!}
                componentId={ComponentConstants.MEMBERS}
                paymentConfig={paymentConfig}
                paymentStatus={paymentStatus}
                callbacks={paymentCallbacks}
                title="Membership Payment"
                showMemberDetails={true}
                type='member'
                existingInvoiceId={existingMemberInvoice?.id}
                existingInvoice={existingMemberInvoice}
              />
            </div>
          </div>

        );
      case MemberRegistrationStepConstants.COMPLETE:
        return null;
      default:
        return <p>Loading...</p>;
    }
  };

  useEffect(() => {
    if (member?.memberRegistrationStepId === MemberRegistrationStepConstants.COMPLETE) {
      handleMembershipCompletion();
    }
  }, [member?.memberRegistrationStepId]);

  const handleMembershipCompletion = async () => {
    try {
      if (refreshToken) {
        const response = await refreshUserToken(refreshToken);
        console.log(response);
        if (response) {
          navigate('/admin/dashboard');
        } else {
          console.error('Token refresh failed');
        }
      } else {
        console.error('No refresh token available');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
    }
  };

  const fetchMember = async () => {
    try {
      const response = await getById(memberId || '');
      const fetchedMember: MemberDTO | undefined = response?.data;
      setMember(fetchedMember);

      if (
        fetchedMember?.memberRegistrationStepId !=
        MemberRegistrationStepConstants.OTP &&
        fetchedMember?.memberRegistrationStepId !=
        MemberRegistrationStepConstants.WELCOME
      ) {
        if (
          fetchedMember?.memberRegistrationStepId !=
          MemberRegistrationStepConstants.MEMBERSHIP
        ) {
          setMemberType(fetchedMember?.memberType);
          setMemberCategory(fetchedMember?.memberCategory);
        }
        // Note: MEMBERSHIP step memberType is now fetched by useMemberTypeById hook
      }
    } catch (error) {
      console.error('Error fetching User', error);
    }
  };

  // Effect to set memberType when TanStack Query fetches it
  useEffect(() => {
    if (fetchedMemberTypeData) {
      setMemberType(fetchedMemberTypeData);
    }
  }, [fetchedMemberTypeData]);

  useEffect(() => {
    fetchMember();
  }, [memberId, member?.memberRegistrationStepId]);

  // Re-sync when the member returns to this tab, so a tab left open while paying elsewhere never acts on stale data
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchMember();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [memberId]);

  // While a card payment is processing, poll so the page completes by itself once the PayFast notification lands
  useEffect(() => {
    if (member?.memberRegistrationStepId !== MemberRegistrationStepConstants.THREE_D_REDIRECT) return;
    const interval = setInterval(fetchMember, 10000);
    return () => clearInterval(interval);
  }, [memberId, member?.memberRegistrationStepId]);

  return (
    <AdminLayout
      activeMenuItem="/admin/membersetup"
      breadcrumbs={breadcrumbs}
      allowedRoles={['User']}
      requiredPermission="Main.MemberSetup.View"
    >
      <div className="w-4/5 mx-auto mt-10">
        <Stepper currentStep={currentStepNumber} steps={steps} />
      </div>
      <div className="mt-5 sm:mt-8">{renderStepContent()}</div>
    </AdminLayout>
  );
};

export default MemberSetup;
